import { Box, Fade, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '../components/atomic';
import SEO from '../components/SEO';
import { StyledButton } from '../components/ui';
import { LegalPageLayout } from '../layouts';

/**
 * 404 Not Found page component
 *
 * Dark navy aesthetic matching the Homepage/About/Product pages:
 * - Full-height dark navy background
 * - Large 404 number in accent green
 * - White text for headings and body
 * - Consistent button styling
 * - Helpful navigation links with proper hover states
 *
 * @returns {JSX.Element} 404 error page
 */
function NotFound() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <LegalPageLayout>
      <SEO
        title="Page Not Found"
        description="This page does not exist or has been moved. Navigate back to the EllaFin homepage, learn about the product, or join the early access program."
        noIndex={true}
      />

      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          backgroundColor: '#1e2140',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Fade in={isLoaded} timeout={600}>
              <Box>
                {/* 404 Number */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '6rem', sm: '8rem', md: '10rem' },
                    fontWeight: 900,
                    color: theme.palette.secondary.main,
                    lineHeight: 0.8,
                    mb: 2,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                >
                  404
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={800}>
              <Box>
                {/* Error Message */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    fontWeight: 600,
                    color: '#ffffff',
                    mb: 2,
                    fontFamily: '"Playfair Display", serif',
                  }}
                >
                  Page Not Found
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={1000}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.125rem',
                    color: alpha('#ffffff', 0.7),
                    mb: 4,
                    maxWidth: '500px',
                    mx: 'auto',
                    lineHeight: 1.6,
                  }}
                >
                  The page you're looking for doesn't exist or has been moved.
                  Let's get you back to exploring our solutions.
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <StyledButton
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/')}
                    sx={{ minWidth: '160px' }}
                  >
                    Go Home
                  </StyledButton>
                </Box>

                {/* Helpful Links */}
                <Box
                  sx={{
                    mt: 6,
                    pt: 4,
                    borderTop: `1px solid ${alpha('#ffffff', 0.1)}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: alpha('#ffffff', 0.9) }}
                  >
                    Looking for something specific?
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 3,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      component="button"
                      onClick={() => navigate('/about')}
                      sx={{
                        color: alpha('#ffffff', 0.7),
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        '&:hover': { color: theme.palette.secondary.main },
                      }}
                    >
                      About EllaFin
                    </Typography>

                    <Typography
                      component="a"
                      href="https://www.rsmc.ai/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: alpha('#ffffff', 0.7),
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        '&:hover': { color: theme.palette.secondary.main },
                      }}
                    >
                      RSM Collective
                    </Typography>

                    <Typography
                      component="button"
                      onClick={() => navigate('/early-access')}
                      sx={{
                        color: alpha('#ffffff', 0.7),
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        '&:hover': { color: theme.palette.secondary.main },
                      }}
                    >
                      Get Early Access
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>
    </LegalPageLayout>
  );
}

export default NotFound;
